import { supabase } from '@/lib/supabase';
import type {
  ProjectRow, RoomRow, SpecRow, RoomImageRow, PaletteColorRow,
  ScheduleEntryRow, DesignerContactRow, PortalViewScope,
} from '@/types/database';

export interface ContractorView {
  // The contacts library shape (0018) — was typed against the legacy table,
  // which 0029 drops. The payload's eight fields all exist on the library row.
  contact: Pick<DesignerContactRow,
    'id' | 'name' | 'trade' | 'company' | 'phone' | 'email' | 'role' | 'is_subcontractor'>;
  view_scope: PortalViewScope;
  project: Pick<ProjectRow,
    'id' | 'name' | 'client_name' | 'address' | 'start_date' | 'target_completion_date'>;
  rooms: Pick<RoomRow,
    'id' | 'name' | 'status' | 'notes' | 'hero_image_url' | 'completed_at' | 'is_exterior'>[];
  specs: Pick<SpecRow,
    'id' | 'room_id' | 'item' | 'vendor' | 'quantity' | 'unit'
    | 'unit_cost_cents' | 'status' | 'estimated_delivery' | 'notes'
    | 'source_url' | 'thumbnail_url' | 'sort_order'>[];
  room_images: Pick<RoomImageRow,
    'id' | 'room_id' | 'kind' | 'storage_path' | 'caption' | 'sort_order'>[];
  palette: Pick<PaletteColorRow,
    'id' | 'room_id' | 'name' | 'hex' | 'vendor' | 'code' | 'sort_order'>[];
  schedule: Pick<ScheduleEntryRow,
    'id' | 'room_id' | 'task' | 'start_date' | 'end_date' | 'status' | 'notes'>[];
}

export async function fetchContractorView(token: string): Promise<ContractorView | null> {
  const { data, error } = await supabase.rpc('get_contractor_view', { token });
  if (error) throw error;
  return (data as ContractorView | null) ?? null;
}

/**
 * Whose firm sent this contractor link (0037) — the name only; this portal
 * carries no logo or tagline (decisions log #7). Null for a dead link.
 */
export async function fetchContractorFirm(token: string): Promise<{ firm_name: string; company_name: string | null } | null> {
  const { data, error } = await supabase.rpc('get_contractor_firm', { token });
  if (error) throw error;
  return (data as { firm_name: string; company_name: string | null } | null) ?? null;
}
